import { Link, useNavigate } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState, type KeyboardEvent, type ReactNode } from 'react';
import { MAX_INTERVIEW_ROUNDS, defaultRound, roundSummary, templateRoundSchema, type InterviewPlanOptions, type PlanTemplateView, type TemplateRound } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Field, FieldRow } from '../../../../components/Field';
import { Input, Textarea } from '../../../../components/Input';
import { SkeletonLines } from '../../../../components/Skeleton';
import { EmptyState, ForbiddenState, NotFoundState } from '../../../../components/system-states/SystemState';
import { ApiError, errorMessage } from '../../../../lib/api-client';
import { useCanEdit, useCanView } from '../../../../stores/session';
import { useToasts } from '../../../../stores/toasts';
import { usePlanOptions, usePlanTemplates, useSavePlanTemplate } from '../../api/evaluation-api';
import { RoundEditor, roundErrors } from '../RoundEditor';
import { useLeaveGuard } from './LeaveGuard';
import styles from './Evaluation.module.css';

/**
 * ADMIN-03 (F19.03), admin redesign batch 2 Option A: the plans as a strip on top, then the open plan's name and "When to use
 * it", and its rounds side by side — up to three, each with how it's held and a panel of up to five with the shares (10–80%,
 * 100% in all; the drawing leaves the shares out, the rule keeps them). A job copies a plan in its round setup ("Start from a
 * plan"); editing it here never changes a job (#4). Not drawn back from the old drawing: a typed name, length, rubric or kit
 * per round, decision rule, Lead/Observer, pass mark, blind, scorecards due, self-booking — the kit is picked on the job's
 * round. Rounds keep their order (no dragging).
 */
export function PlansWorkspace({ selectedId }: { readonly selectedId: string | null }): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const plans = usePlanTemplates(canView);
  const options = usePlanOptions(canView);
  const navigate = useNavigate();

  if (!canView) return <ForbiddenState resource="interview plans" contact="Your admin" />;

  const rows = plans.data ?? [];
  const isNew = selectedId === 'new';
  const openId = isNew ? null : (selectedId ?? rows[0]?.id ?? null);
  const open = rows.find((row) => row.id === openId) ?? null;

  const newButton = canEdit ? (
    <Button variant="solid" size="sm" icon={Plus} onClick={() => void navigate({ to: '/admin/interview-plans/$planId', params: { planId: 'new' } })}>
      New plan
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <h1 className={styles.title}>
          Interview plans {plans.data && <span className={styles.count}>{rows.length}</span>}
        </h1>
        <div className={styles.headActions}>{rows.length > 0 && !isNew && newButton}</div>
      </header>
      <p className={styles.lede}>Saved rounds and panels a job can start from. A job copies the plan; editing it here never changes a job.</p>

      {plans.isPending ? (
        <SkeletonLines count={8} />
      ) : rows.length === 0 && !isNew ? (
        <EmptyState isInset title="No interview plans yet" body="A saved plan fills in a job's rounds and panels in one click." action={newButton ?? undefined} />
      ) : (
        <>
          {rows.length > 0 && (
            <nav className={styles.strip} aria-label="Interview plans">
              {rows.map((row) => (
                <Link key={row.id} to="/admin/interview-plans/$planId" params={{ planId: row.id }} className={styles.stripItem} aria-current={!isNew && row.id === openId ? 'page' : undefined}>
                  <span className={styles.stripName}>
                    {row.name} <span className={styles.stripMeta}>{row.rounds.length} round{row.rounds.length === 1 ? '' : 's'}</span>
                  </span>
                  <span className={styles.stripSummary}>{row.rounds.map(roundSummary).join(' → ')}</span>
                </Link>
              ))}
            </nav>
          )}
          {isNew ? (
            <PlanEditor key="new" plan={null} options={options.data} knownIds={rows.map((row) => row.id)} />
          ) : open ? (
            <PlanEditor key={open.id} plan={open} options={options.data} knownIds={rows.map((row) => row.id)} />
          ) : (
            <NotFoundState />
          )}
        </>
      )}
    </div>
  );
}

interface RoundDraft {
  readonly rowId: string;
  readonly round: TemplateRound;
}

interface Draft {
  readonly name: string;
  readonly description: string;
  readonly rounds: readonly RoundDraft[];
}

function draftOf(plan: PlanTemplateView | null): Draft {
  return {
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    // Keyed by place: rounds are never reordered, and only the last one comes off.
    rounds: (plan && plan.rounds.length > 0 ? plan.rounds : [defaultRound(0)]).map((round, index) => ({ rowId: `round-${index}`, round })),
  };
}

/** What still stops a save, in plain words; empty when it can go. */
export function planProblems(draft: Draft): readonly string[] {
  const problems: string[] = [];
  if (draft.name.trim().length < 2) problems.push('Name the plan.');
  if (draft.rounds.some(({ round }) => !templateRoundSchema.safeParse(round).success)) problems.push("Fix the rounds' shares.");
  return problems;
}

function PlanEditor({ plan, options, knownIds }: { readonly plan: PlanTemplateView | null; readonly options: InterviewPlanOptions | undefined; readonly knownIds: readonly string[] }): ReactNode {
  const canEdit = useCanEdit('hiring_setup');
  const save = useSavePlanTemplate();
  const navigate = useNavigate();
  const show = useToasts((state) => state.show);
  // Untouched, the editor shows the plan as the server has it now, so another admin's save simply appears. The first change
  // takes a copy and remembers the version it came from; that version goes with the save, so a save by someone else in
  // between is refused (409) instead of being overwritten (review W7).
  const [edit, setEdit] = useState<{ readonly base: PlanTemplateView | null; readonly draft: Draft } | null>(null);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  // Our own save moves the plan's version on before its answer is back; that is not someone else's.
  const [isSaving, setIsSaving] = useState(false);
  const isDirty = edit !== null;
  const { prompt, release } = useLeaveGuard(isDirty);
  const base = edit ? edit.base : plan;
  const draft = edit ? edit.draft : draftOf(plan);
  const isOvertaken = isDirty && base !== null && plan !== null && plan.version !== base.version && !isSaving;

  const isReadOnly = !canEdit;
  const problems = planProblems(draft);

  const change = (patch: Partial<Draft>): void =>
    setEdit((previous) => ({ base: previous ? previous.base : plan, draft: { ...(previous ? previous.draft : draftOf(plan)), ...patch } }));
  const setRound = (index: number, round: TemplateRound): void => change({ rounds: draft.rounds.map((draftRound, at) => (at === index ? { ...draftRound, round } : draftRound)) });

  /** Back to the plan as it is now — Cancel, or "load theirs" after someone else saved. */
  function discard(): void {
    setEdit(null);
    setErrors({});
  }

  function cancel(): void {
    if (!plan) {
      release();
      void navigate({ to: '/admin/interview-plans' });
      return;
    }
    discard();
  }

  async function onSave(): Promise<void> {
    if (problems.length > 0 || isReadOnly) return;
    const before = new Set(knownIds);
    setIsSaving(true);
    try {
      const saved = await save.mutateAsync({
        id: base?.id ?? null,
        version: base?.version ?? 0,
        body: { name: draft.name.trim(), description: draft.description.trim() || null, rounds: draft.rounds.map(({ round }) => round) },
      });
      const made = base ? null : saved.find((row) => !before.has(row.id));
      release();
      discard();
      setIsSaving(false);
      show({ tone: 'success', message: `${draft.name.trim()} saved`, detail: 'Jobs that already used it keep their own copy.' });
      if (made) void navigate({ to: '/admin/interview-plans/$planId', params: { planId: made.id } });
    } catch (cause) {
      setIsSaving(false);
      setErrors(cause instanceof ApiError ? cause.fields : {});
      show({ tone: 'danger', message: 'The interview plan was not saved', detail: errorMessage(cause) });
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey) && isDirty) {
      event.preventDefault();
      void onSave();
    }
  }

  return (
    <section className={styles.editor} aria-label={base ? base.name : 'New interview plan'} onKeyDown={onKeyDown}>
      <header className={styles.editorHead}>
        <div className={styles.editorTitleRow}>
          <h2 className={styles.editorTitle}>{base ? base.name : 'New interview plan'}</h2>
        </div>
        {canEdit && (
          <div className={styles.headActions}>
            {isDirty && <span className={problems.length > 0 ? styles.problemText : styles.quiet} role="status">{problems.length > 0 ? problems.join(' ') : 'Unsaved changes'}</span>}
            <Button variant="ghost" size="sm" disabled={!isDirty && base !== null} onClick={cancel}>
              Cancel
            </Button>
            <Button variant="solid" size="sm" disabled={!isDirty || problems.length > 0} isPending={save.isPending} onClick={() => void onSave()}>
              Save plan
            </Button>
          </div>
        )}
      </header>

      {prompt}
      {isOvertaken && (
        <p className={styles.bannerDanger} role="alert">
          Someone else saved this plan while you were editing, so saving yours will be refused.{' '}
          <button type="button" className={styles.bannerButton} onClick={discard}>
            Drop my changes and load theirs
          </button>
        </p>
      )}

      <div className={styles.form}>
        <FieldRow>
          <Field label="Name" isRequired>
            {({ inputId }) => <Input id={inputId} disabled={isReadOnly} value={draft.name} placeholder="Standard two rounds" onChange={(event) => change({ name: event.target.value })} />}
          </Field>
          <Field label="When to use it" isOptional>
            {({ inputId }) => <Textarea id={inputId} rows={2} disabled={isReadOnly} value={draft.description} placeholder="Most office roles: one skills round, then a team round" onChange={(event) => change({ description: event.target.value })} />}
          </Field>
        </FieldRow>
      </div>

      {options === undefined ? (
        <SkeletonLines count={6} />
      ) : (
        <ol className={styles.planRounds}>
          {draft.rounds.map(({ rowId, round }, index) => (
            <RoundEditor
              key={rowId}
              round={round}
              index={index}
              options={options}
              isReadOnly={isReadOnly}
              errors={roundErrors(errors, index)}
              onChange={(next) => setRound(index, next)}
              {...(index === draft.rounds.length - 1 && index > 0 ? { onRemove: () => change({ rounds: draft.rounds.slice(0, -1) }) } : {})}
            />
          ))}
          {!isReadOnly && draft.rounds.length < MAX_INTERVIEW_ROUNDS && (
            <li className={styles.addRound}>
              <Button variant="outline" size="sm" icon={Plus} onClick={() => change({ rounds: [...draft.rounds, { rowId: crypto.randomUUID(), round: defaultRound(draft.rounds.length) }] })}>
                Add round {draft.rounds.length + 1}
              </Button>
              <span className={styles.quiet}>Up to three. A job that starts from this plan gets these rounds, panels and shares included.</span>
            </li>
          )}
        </ol>
      )}
      <p className={styles.quiet}>Each job picks its rounds' interview kits in its own round setup, not here.</p>
    </section>
  );
}
